/**
 * IA Product Gallery — <ia-product-gallery>
 * --------------------------------------------------------------------
 * Main image strip (CSS scroll-snap, swipeable on touch) + thumbnail rail
 * with prev/next arrows. The rail is vertical beside the image on desktop
 * and horizontal below it on mobile (layout is CSS-only, see
 * assets/ia-product-spotlight.css); this class only syncs state.
 *
 * Markup (rendered by sections/ia-product-spotlight.liquid):
 *   <ia-product-gallery data-loop="true">
 *     <div data-ia-track>          scroll-snap container
 *       <div data-ia-slide>…</div>  one per image
 *     </div>
 *     <button data-ia-prev>…</button>
 *     <div data-ia-thumbs>
 *       <button data-ia-thumb>…</button>
 *     </div>
 *     <button data-ia-next>…</button>
 *     <span data-ia-status>1 / 9</span>   (optional, aria-live)
 *   </ia-product-gallery>
 *
 * data-loop="true"  wraps around at either end (arrows never disable).
 */
if (!customElements.get('ia-product-gallery')) {
  class IaProductGallery extends HTMLElement {
    constructor() {
      super();
      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.index = 0;
      this.pendingLeft = null;
    }

    connectedCallback() {
      this.track = this.querySelector('[data-ia-track]');
      this.slides = Array.from(this.querySelectorAll('[data-ia-slide]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-ia-thumb]'));
      this.rail = this.querySelector('[data-ia-thumbs]');
      this.prevBtn = this.querySelector('[data-ia-prev]');
      this.nextBtn = this.querySelector('[data-ia-next]');
      this.status = this.querySelector('[data-ia-status]');
      this.loop = this.dataset.loop === 'true';

      if (!this.track || this.slides.length < 2) {
        this.classList.add('ia-pg--single');
        return;
      }

      this.thumbs.forEach((thumb, i) => thumb.addEventListener('click', () => this.goTo(i)));
      if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
      if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      this.track.addEventListener('keydown', this.onKeydown);
      window.addEventListener('resize', this.onResize);

      this.setActive(0, false);
    }

    disconnectedCallback() {
      clearTimeout(this.pendingTimer);
      if (this.track) {
        this.track.removeEventListener('scroll', this.onScroll);
        this.track.removeEventListener('keydown', this.onKeydown);
      }
      window.removeEventListener('resize', this.onResize);
    }

    get count() {
      return this.slides.length;
    }

    get reducedMotion() {
      return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /* Track scrolled (swipe / wheel / programmatic): derive the active index.
       While a programmatic scroll is in flight the intermediate positions are
       ignored so the active thumb doesn't flicker back and forth. */
    onScroll() {
      cancelAnimationFrame(this.scrollRaf);
      this.scrollRaf = requestAnimationFrame(() => {
        if (this.pendingLeft !== null) {
          if (Math.abs(this.track.scrollLeft - this.pendingLeft) > 1) return;
          this.endProgrammaticScroll();
        }
        const width = this.track.clientWidth || 1;
        const i = Math.max(0, Math.min(this.count - 1, Math.round(this.track.scrollLeft / width)));
        if (i !== this.index) this.setActive(i, true);
      });
    }

    /* Programmatic scroll: snapping is paused for the duration so the browser
       can't stop the animation at an intermediate snap point (Chrome does this
       when jumping more than one slide), then restored once the target is reached. */
    startProgrammaticScroll(left) {
      this.pendingLeft = left;
      this.track.style.scrollSnapType = 'none';
      clearTimeout(this.pendingTimer);
      this.pendingTimer = setTimeout(() => this.endProgrammaticScroll(), 1000);
    }

    endProgrammaticScroll() {
      clearTimeout(this.pendingTimer);
      if (this.pendingLeft === null) return;
      this.pendingLeft = null;
      this.track.style.scrollSnapType = '';
    }

    /* Width changed: re-align the track on the current slide */
    onResize() {
      cancelAnimationFrame(this.resizeRaf);
      this.resizeRaf = requestAnimationFrame(() => {
        this.endProgrammaticScroll();
        this.track.scrollTo({ left: this.index * this.track.clientWidth, behavior: 'auto' });
      });
    }

    onKeydown(event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.step(-1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.step(1);
      }
    }

    step(direction) {
      let i = this.index + direction;
      if (this.loop) {
        i = (i + this.count) % this.count;
      } else {
        i = Math.max(0, Math.min(this.count - 1, i));
      }
      this.goTo(i);
    }

    goTo(i) {
      const left = i * this.track.clientWidth;
      this.setActive(i, true);
      if (Math.abs(this.track.scrollLeft - left) < 1) return;
      this.startProgrammaticScroll(left);
      this.track.scrollTo({ left, behavior: this.reducedMotion ? 'auto' : 'smooth' });
    }

    setActive(i, smooth) {
      this.index = i;
      this.thumbs.forEach((thumb, j) => {
        const active = j === i;
        thumb.classList.toggle('is-active', active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
      });
      if (!this.loop) {
        if (this.prevBtn) this.prevBtn.disabled = i === 0;
        if (this.nextBtn) this.nextBtn.disabled = i === this.count - 1;
      }
      if (this.status) this.status.textContent = `${i + 1} / ${this.count}`;
      this.revealThumb(i, smooth);
    }

    /* Scroll the rail (not the page) so the active thumbnail is in view */
    revealThumb(i, smooth) {
      const rail = this.rail;
      const thumb = this.thumbs[i];
      if (!rail || !thumb) return;
      const behavior = smooth && !this.reducedMotion ? 'smooth' : 'auto';
      const vertical = getComputedStyle(rail).flexDirection === 'column';

      if (vertical) {
        const top = thumb.offsetTop;
        const bottom = top + thumb.offsetHeight;
        if (top < rail.scrollTop) {
          rail.scrollTo({ top, behavior });
        } else if (bottom > rail.scrollTop + rail.clientHeight) {
          rail.scrollTo({ top: bottom - rail.clientHeight, behavior });
        }
      } else {
        const left = thumb.offsetLeft;
        const right = left + thumb.offsetWidth;
        if (left < rail.scrollLeft) {
          rail.scrollTo({ left, behavior });
        } else if (right > rail.scrollLeft + rail.clientWidth) {
          rail.scrollTo({ left: right - rail.clientWidth, behavior });
        }
      }
    }
  }

  customElements.define('ia-product-gallery', IaProductGallery);
}
